export const ROLES = {
  TENANT_VIEWER: 'TENANT_VIEWER',
  TENANT_ADMIN_VIEWER: 'TENANT_ADMIN_VIEWER',
  TENANT_ADMIN: 'TENANT_ADMIN',
  TENANT_DATA_VIEWER: 'TENANT_DATA_VIEWER',
  CUSTOMER_ADMIN: 'CUSTOMER_ADMIN',
  PORTAL_ADMIN: 'PORTAL_ADMIN',
  INVOICE_COMPARE: 'INVOICE_COMPARE',
  TENANT_VAT_VIEWER: 'TENANT_VAT_VIEWER',
} as const

export const PERMISSIONS = {
  TENANT_ACCESS: 'tenant:access',
  SETTINGS_READ: 'tenant:settings:read',
  SETTINGS_WRITE: 'tenant:settings:write',
  MICROTECH_DATA_READ: 'tenant:microtech-data:read',
  OPENSEARCH_READ: 'tenant:opensearch:read',
  VAT_VALIDATION_READ: 'tenant:vat-validation:read',
  VAT_VALIDATION_RUN: 'tenant:vat-validation:run',
  WORKFLOW_ADMIN: 'workflow:admin',
  PORTAL_ADMIN: 'portal:admin',
  CUSTOMER_SETTINGS_READ: 'customer:settings:read',
  CUSTOMER_SETTINGS_WRITE: 'customer:settings:write',
} as const

export function hasRole(roles: string[] | undefined, role: string): boolean {
  return !!roles?.includes(role)
}

export function isPortalAdmin(roles: string[] | undefined): boolean {
  return hasRole(roles, ROLES.PORTAL_ADMIN)
}

export function hasPermission(permissions: string[] | undefined, permission: string): boolean {
  if (!permissions) return false
  return permissions.includes('*') || permissions.includes(permission)
}

export function hasAnyPermission(permissions: string[] | undefined, requiredPermissions: string[]): boolean {
  if (!permissions) return false
  if (permissions.includes('*')) return true
  return requiredPermissions.some((p) => permissions.includes(p))
}

// A tenant group maps to one or more roles. Stored mappings may still hold the
// legacy single-role string, so readers normalize through rolesOf.
export type GroupRoleMapping = Record<string, string | string[]>

export function rolesOf(value: string | string[] | undefined | null): string[] {
  const roles = Array.isArray(value) ? value : [value]
  return roles.map((r) => r?.trim() ?? '').filter(Boolean)
}

// withAssignedRoles extends the selectable role options by roles a group already
// has but the options do not list (e.g. legacy names), so they stay visible.
export function withAssignedRoles<T extends { label: string; value: string }>(
  options: T[],
  assigned: string[],
): { label: string; value: string }[] {
  const known = new Set(options.map((o) => o.value))
  const extra = assigned.filter((r) => !known.has(r)).map((r) => ({ label: r, value: r }))
  return [...options, ...extra]
}
